import { ArrayOfAll } from '../../utils/ArrayOfAll';
import {
    AgencyAlertKind,
    CaseloadReading,
    DomainState,
    IInsightsFilter,
    READING_SENTENCE_LABEL
} from '@innerhive-internal/innerhive-common';

/**
 * One status palette for the whole page, so a colour means the same thing wherever it appears: in
 * the caseload honeycomb, in the pill under someone's diagram, in a facet of it, against a note in
 * the detail view, and beside a name in every list.
 *
 * The brand's red and yellow, and a green of the brand's hue. Red is something that already happened. Yellow is more raised than this
 * person's usual, which is worth watching. Green is steady, and doubles as the ordinary observation
 * on the weekly chart. Grey is the one that encodes no claim at all: not enough to compare, which is
 * why it is the only recessive colour here.
 *
 * None of these carry small text on their own: the pills take the darker brand shades for white
 * text, or dark text on the yellow (READING_PILL), and every surface ships its label beside the
 * colour, because a status colour alone is never allowed to be the only thing carrying the meaning.
 */
export const STATUS = {
    // The brand's darker red, a shade under the theme red, so the rim reads as a notification.
    incident: '#CF3C4F',
    worthWatching: '#F2C31A',
    steady: '#10976A',
    // Low chroma on purpose, so a client nobody knows enough about recedes instead of competing with
    // the three readings that do mean something.
    unknown: '#7E8A8F'
};

/** The map, the pill, the mark beside a name: one colour per reading, read off the shared palette. */
export const READING_COLOR: { [key in CaseloadReading]: string } = {
    incident: STATUS.incident,
    worthWatching: STATUS.worthWatching,
    steady: STATUS.steady,
    unknown: STATUS.unknown
};

/**
 * The reading as a pill carrying its label. White on the steady green is 3.7:1, under the 4.5:1
 * small text needs, so it takes the brand's darker shade of the same hue; the yellow takes the
 * brand's own dark contrast colour instead of white. The red is already the darker shade.
 */
export const READING_PILL: { [key in CaseloadReading]: { background: string; color: string } } = {
    incident: { background: STATUS.incident, color: '#fff' },
    worthWatching: { background: STATUS.worthWatching, color: '#032A37' },
    steady: { background: '#04845C', color: '#fff' },
    // The unknown grey darkened until white text on it reads at 5.18:1, which the map's grey does not.
    unknown: { background: '#636F74', color: '#fff' }
};

export const KIND_ORDER: ArrayOfAll<AgencyAlertKind> = ['incident', 'baseline', 'observation'];

export const KIND_COLOR: { [key in AgencyAlertKind]: string } = {
    incident: STATUS.incident,
    baseline: STATUS.worthWatching,
    // The ordinary case takes the steady green rather than a recessive grey. On most weeks it is the
    // bulk of the bar, and a chart whose common case is drawn as absence reads as broken, not calm.
    observation: STATUS.steady
};

/**
 * The resting fill of a bar: the same hue at three quarters strength over white, so a column reads as
 * a solid lighter version of its kind rather than a see-through one. Named colours rather than an
 * opacity so two touching bands of a stacked column stay distinct, and the legend carries a label
 * for each.
 */
export const KIND_TINT: { [key in AgencyAlertKind]: string } = {
    incident: '#F07383',
    baseline: '#F5D253',
    observation: '#4CB18F'
};

export const KIND_LABEL: { [key in AgencyAlertKind]: string } = {
    incident: 'Incident',
    baseline: 'Worth Watching',
    observation: 'Observations'
};

/** The same labels for use mid-sentence. Anything writing a kind into running text uses these. */
export const KIND_SENTENCE_LABEL: { [key in AgencyAlertKind]: string } = {
    incident: 'incident',
    baseline: 'worth watching',
    observation: 'observation'
};

/** What the kind means, for the legend's second line. Plain language, no jargon. */
export const KIND_DESCRIPTION: { [key in AgencyAlertKind]: string } = {
    incident: 'Something already happened: a fall, an ER trip, a hospital stay.',
    baseline: 'A change against how this client usually is.',
    observation: 'Everything else the record picked up, including anything missing on file.'
};

/**
 * The agency header's own palette, brought down into the tab so the two read as one surface rather
 * than a branded band sitting on top of a generic dashboard. The deep teal carries headings and
 * structure; the coral is used sparingly, as an accent on the things that want attention.
 *
 * Distinct from the data hues above on purpose: these dress the chrome and never encode a value, so
 * they are free to be as dark or as saturated as the brand is.
 */
export const BRAND = {
    deepTeal: '#12414f',
    deeperTeal: '#0d3542',
    coral: '#EE6C4E',
    /**
     * The same hue at 5.15:1 on white, for the places it has to carry meaning on its own: a small
     * mark, or white text sitting on it. The brand coral is 3.05:1 and cannot.
     */
    coralInk: '#B84B2A',
    /** A wash of the deep teal, for card and chip surfaces that should read as part of the band. */
    tealWash: 'rgba(18, 65, 79, 0.045)',
    tealEdge: 'rgba(18, 65, 79, 0.14)',
    coralWash: 'rgba(238, 108, 78, 0.10)'
};

/**
 * A status ramp, not a categorical one: three named states of one part of daily life, in worsening
 * order, drawn in the three reading colours so a wedge and the pill beside it never disagree about
 * what a colour means. Every state still ships with its label and a different fill length, because
 * a status colour on its own is never allowed to be the only thing carrying the meaning.
 */
export const DOMAIN_STATE_COLOR: { [key in DomainState]: string } = {
    usual: STATUS.steady,
    drifting: STATUS.worthWatching,
    sharp: STATUS.incident
};

/** The unfilled part of a wedge: what this person usually holds, that they are not holding now. */
export const DOMAIN_TRACK = '#D8D4CE';

/** Ink and furniture, kept recessive so the marks carry the chart. */
export const CHART_INK = {
    axis: 'var(--ion-color-step-250)',
    grid: 'var(--ion-color-step-100)',
    label: 'var(--ion-color-step-600)',
    strongLabel: 'var(--ion-color-step-850)',
    surface: 'var(--ion-background-color)'
};

/** Centre of the hex at a grid position, with odd rows nudged half a cell right so they interlock. */
export const hexCentre = (params: { column: number; row: number; radius: number }): { cx: number; cy: number } => {
    const { column, row, radius } = params;
    const width = Math.sqrt(3) * radius;
    return {
        cx: width * (column + (row % 2 === 1 ? 0.5 : 0)) + width / 2,
        cy: radius * 1.5 * row + radius
    };
};

/**
 * Corners of a FLAT-TOP hexagon, clockwise from the top-left corner of the flat top edge.
 *
 * Flat-top rather than the pointy-top the honeycomb uses, because here the six EDGES carry meaning
 * rather than the cells: a flat-top hexagon's edges face top, upper-right, lower-right, bottom,
 * lower-left and upper-left, which is exactly the six positions the domain labels need.
 */
export const flatTopHexCorners = (params: { cx: number; cy: number; radius: number }): { x: number; y: number }[] => {
    const { cx, cy, radius } = params;
    // Start at 240 degrees so corner 0 is the top-left, making edge 0 the flat top edge.
    return [0, 1, 2, 3, 4, 5].map(index => {
        const radians = ((240 + index * 60) * Math.PI) / 180;
        return {
            x: Math.round((cx + radius * Math.cos(radians)) * 100) / 100,
            y: Math.round((cy + radius * Math.sin(radians)) * 100) / 100
        };
    });
};

/** A point a fraction of the way from one corner to the next. */
export const alongEdge = (from: { x: number; y: number }, to: { x: number; y: number }, fraction: number) => ({
    x: Math.round((from.x + (to.x - from.x) * fraction) * 100) / 100,
    y: Math.round((from.y + (to.y - from.y) * fraction) * 100) / 100
});

/**
 * A narrowing read back as a phrase, so a dimmed page never leaves the reader guessing why.
 *
 * Assembled from the sentence-case labels rather than from the display ones, because it lands
 * mid-sentence, and in a fixed order so that stacking two selections reads the same way whichever
 * of them the reader picked first.
 */
export const describeFilter = (filter: IInsightsFilter): string => {
    const parts: string[] = [];
    if (filter.kind) {
        parts.push(KIND_SENTENCE_LABEL[filter.kind]);
    }
    if (filter.reading) {
        parts.push(`clients who ${READING_SENTENCE_LABEL[filter.reading]}`);
    }
    return parts.join(', ');
};
